import React from 'react';
import { FilterType } from './CandidateFilters';
import AvailabilityFilterInput from './AvailabilityFilterInput';
import LocationFilterInput from './LocationFilterInput';
import { Button } from 'flowbite-react';
import SkillFilterInput from './SkillFilterInput';
import RoleFilterInput from './RoleFilterInput';
import CampusInput from './CampusInput';
import EducationFilterInput from './EducationFilterInput';
import SalaryFilterInput from './SalaryFilterInput';
import StatusFilterInput from './StatusFilterInput';
import MatchFilterInput from './MatchFilterInput';
import { IoFilterSharp } from 'react-icons/io5';
import { useMediaQuery, Zoom } from '@mui/material';
import { Drawer } from 'src/components/shared/Drawer';
import { useSearchParams } from 'react-router-dom';
import BdPocFilterInput from './BdPocFilter';
import ExperienceFilterInput from './ExperienceFilterInput';
import StudentTypeFilterInput from './StudentTypeFilterInput';
import RoundTypeFilterInput from './RoundTypeFilterInput';
import DriveUserStatusFilterInput from './DriveUserStatusFilterInput';
import DriveUserDocumentFilterInput from './DriveUserDocumentFilterInput';
import DriveUserNBFCStatusFilterInput from './DriveUserNBFCStatusFilterInput';
import DriveUserISAStatusFilterInput from './DriveUserISAStatusFilterInput';
import DriveUserPapStatusFilterInput from './DriveUserPapStatusFilterInput';
import DriveAttendanceStatusFilterInput from './DriveAttendanceStatusFilterInput';
import DriveOpportunityStatusFilterInput from './DriveOpportunityStatusFilterInput';
import DriveUserInterviewLabelFilterInput from './DriveUserInterviewLabelFilterInput';
import DriveUserOfferStatusFilterInput from './DriveUserOfferStatusFilterInput';
import DriveLabelFilterInput from './DriveLabelFilterInput';
import MatchedPercentileFilter from './MatchedPercentileFilter';

// Define a type for the filter names
type FilterName =
	| 'skill'
	| 'location'
	| 'availability'
	| 'campus'
	| 'education'
	| 'role'
	| 'status'
	| 'salary'
	| 'match'
	| 'matchedpercentilefilter'
	| 'bdPoc'
	| 'experience'
	| 'studentType'
	| 'roundType'
	| 'driveUserStatus'
	| 'driveUserDocument'
	| 'isaStatus'
	| 'nbfcStatus'
	| 'papStatus'
	| 'attendanceStatus'
	| 'opportunityStatus'
	| 'userInterviewLabel'
	| 'offerStatus'
	| 'driveLabel';

const RenderFilters = ({
	showSkillFilter,
	showLocationFilter,
	showAvailabilityFilter,
	showCampusFilter,
	showEducationFilter,
	showRoleFilter,
	showMatchFilter,
	showMatchedPercentileFilter,
	showStatusFilter,
	showSalaryFilter,
	appliedFilters,
	showFilterDrawer,
	setAppliedFilters,
	setShowFilterDrawer,
	showBdPocFilter,
	filterName = 'Filters',
	showExperienceFilter,
	showStudentTypeFilter,
	showRoundTypeFilter,
	showDriveUserStatusFilter,
	showDriveUserDocumentFilter,
	showIsaStatusFilter,
	showNbfcStatusFilter,
	showPapStatusFilter,
	showOfferStatusFilter,
	showAttendanceStatusFilter,
	showOpportunityStatusFilter,
	showUserInterviewLabelFilter,
	showDriveLabelFilter,
	filterTrailingSlot,
	filterOrder = [
		'skill',
		'experience',
		'location',
		'availability',
		'campus',
		'education',
		'role',
		'status',
		'salary',
		'match',
		'matchedpercentilefilter',
		'bdPoc',
		'driveUserStatus',
		'driveUserDocument',
		'roundType',
		'isaStatus',
		'nbfcStatus',
		'papStatus',
		'attendanceStatus',
		'opportunityStatus',
		'userInterviewLabel',
		'offerStatus',
	], // Default order
}: {
	showSkillFilter: boolean;
	showLocationFilter: boolean;
	showAvailabilityFilter: boolean;
	showCampusFilter: boolean;
	showRoleFilter: boolean;
	showEducationFilter: boolean;
	showMatchFilter: boolean;
	showMatchedPercentileFilter?: boolean;
	showStatusFilter: boolean;
	showSalaryFilter?: boolean;
	filterName?: string;
	showBdPocFilter?: boolean;
	appliedFilters: FilterType;
	setShowFilterDrawer: React.Dispatch<React.SetStateAction<boolean>>;
	showFilterDrawer: boolean;
	setAppliedFilters: React.Dispatch<React.SetStateAction<FilterType>>;
	filterOrder?: FilterName[];
	showStudentTypeFilter?: boolean;
	showExperienceFilter?: boolean;
	showRoundTypeFilter?: boolean;
	showDriveUserStatusFilter?: boolean;
	showDriveUserDocumentFilter?: boolean;
	showIsaStatusFilter?: boolean;
	showNbfcStatusFilter?: boolean;
	showPapStatusFilter?: boolean;
	showOfferStatusFilter?: boolean;
	showAttendanceStatusFilter?: boolean;
	showOpportunityStatusFilter?: boolean;
	showUserInterviewLabelFilter?: boolean;
	showDriveLabelFilter?: boolean;
	/** Pass a function to render differently in the mobile filter drawer vs desktop filter row */
	filterTrailingSlot?: React.ReactNode | ((ctx: { forDrawer: boolean }) => React.ReactNode);
}) => {
	const isMobile = useMediaQuery('(max-width:960px)');
	const [searchParams, setSearchParams] = useSearchParams();

	const resolveFilterTrailingSlot = (forDrawer: boolean) => {
		if (filterTrailingSlot == null) return null;
		if (typeof filterTrailingSlot === 'function') {
			return filterTrailingSlot({ forDrawer });
		}
		return filterTrailingSlot;
	};

	const renderFilter = (filterName: FilterName, key: number, showSelect: boolean = false) => {
		switch (filterName) {
			case 'skill':
				return showSkillFilter ? <SkillFilterInput showSelect={showSelect} key={key} /> : null;
			case 'experience':
				return showExperienceFilter ? <ExperienceFilterInput showSelect={showSelect} key={key} /> : null;
			case 'location':
				return showLocationFilter ? <LocationFilterInput showSelect={showSelect} key={key} /> : null;
			case 'availability':
				return showAvailabilityFilter ? <AvailabilityFilterInput showSelect={showSelect} key={key} /> : null;
			case 'campus':
				return showCampusFilter ? <CampusInput showSelect={showSelect} key={key} /> : null;
			case 'education':
				return showEducationFilter ? <EducationFilterInput showSelect={showSelect} key={key} /> : null;
			case 'role':
				return showRoleFilter ? <RoleFilterInput showSelect={showSelect} key={key} /> : null;
			case 'status':
				return showStatusFilter ? <StatusFilterInput showSelect={showSelect} key={key} /> : null;
			case 'salary':
				return showSalaryFilter ? <SalaryFilterInput showSelect={showSelect} key={key} /> : null;
			case 'match':
				return showMatchFilter ? <MatchFilterInput showSelect={showSelect} key={key} /> : null;
			case 'matchedpercentilefilter':
				return showMatchedPercentileFilter ? <MatchedPercentileFilter showSelect={showSelect} key={key} /> : null;
			case 'bdPoc':
				return showBdPocFilter ? <BdPocFilterInput showSelect={showSelect} key={key} /> : null;
			case 'studentType':
				return showStudentTypeFilter ? <StudentTypeFilterInput showSelect={showSelect} key={key} /> : null;
			case 'roundType':
				return showRoundTypeFilter ? <RoundTypeFilterInput showSelect={showSelect} key={key} /> : null;
			case 'driveUserStatus':
				return showDriveUserStatusFilter ? <DriveUserStatusFilterInput showSelect={showSelect} key={key} /> : null;
			case 'driveUserDocument':
				return showDriveUserDocumentFilter ? <DriveUserDocumentFilterInput showSelect={showSelect} key={key} /> : null;
			case 'isaStatus':
				return showIsaStatusFilter ? <DriveUserISAStatusFilterInput showSelect={showSelect} key={key} /> : null;
			case 'nbfcStatus':
				return showNbfcStatusFilter ? <DriveUserNBFCStatusFilterInput showSelect={showSelect} key={key} /> : null;
			case 'papStatus':
				return showPapStatusFilter ? <DriveUserPapStatusFilterInput showSelect={showSelect} key={key} /> : null;
			case 'attendanceStatus':
				return showAttendanceStatusFilter ? (
					<DriveAttendanceStatusFilterInput showSelect={showSelect} key={key} />
				) : null;
			case 'opportunityStatus':
				return showOpportunityStatusFilter ? (
					<DriveOpportunityStatusFilterInput showSelect={showSelect} key={key} />
				) : null;
			case 'userInterviewLabel':
				return showUserInterviewLabelFilter ? (
					<DriveUserInterviewLabelFilterInput showSelect={showSelect} key={key} />
				) : null;
			case 'offerStatus':
				return showOfferStatusFilter ? <DriveUserOfferStatusFilterInput showSelect={showSelect} key={key} /> : null;
			case 'driveLabel':
				return showDriveLabelFilter ? <DriveLabelFilterInput showSelect={showSelect} key={key} /> : null;
			default:
				return null;
		}
	};

	return (
		<>
			{/* Desktop view */}
			<div className="hidden xl:flex items-center flex-wrap justify-between gap-[20px]">
				<div className="flex-1 max-w-full w-full flex items-center flex-wrap gap-[14px]">
					<div className="text-[#28293D] text-sm font-[500]">Filter by:</div>
					{filterOrder
						.filter(f => f !== 'match')
						.map((filterName, i) => {
							const filter = renderFilter(filterName, i);
							return filter ? <div className="w-fit">{filter}</div> : null;
						})}
					{filterTrailingSlot != null ? (
						<div className="w-fit flex items-center flex-wrap gap-[14px]">
							{resolveFilterTrailingSlot(false)}
						</div>
					) : null}
				</div>
				{showMatchFilter && (
					<div className="max-w-fit flex-1 flex items-center gap-2">
						<div className="text-[#28293D] text-sm font-[500]">Sort by:</div>
						<div>
							<MatchFilterInput />
						</div>
					</div>
				)}
			</div>

			<div className="xl:hidden">
				<Button size="sm" color="light" className="!py-1" onClick={() => setShowFilterDrawer(true)}>
					<span className="flex items-center gap-2">
						<span>
							<IoFilterSharp className="w-[20px] h-[20px]" />
						</span>
						{filterName ? <span>{filterName}</span> : null}

						<Zoom in={!!appliedFilters?.length} unmountOnExit>
							<span className="bg-[#7777E7] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px] rounded-full text-white text-[8px] font-[700] flex items-center justify-center">
								{appliedFilters?.length}
							</span>
						</Zoom>
					</span>
				</Button>
			</div>

			<Drawer
				fullWidth={true}
				isModal={!isMobile}
				minHeight="auto"
				isOpen={showFilterDrawer}
				onClose={() => setShowFilterDrawer(false)}
				header={<></>}
				body={
					<div className="px-5 py-4">
						<div className="text-[#1C1C28] text-[18px] font-[500]">Filter by</div>
						<div className="w-full flex flex-col gap-4 whitespace-pre pt-5">
							{filterOrder.map((filterName, i) => renderFilter(filterName, i, true))}
							{filterTrailingSlot != null ? (
								<div className="whitespace-normal border-t border-[#E4E4EB] dark:border-gray-600 pt-4">
									{resolveFilterTrailingSlot(true)}
								</div>
							) : null}
						</div>
					</div>
				}
				footer={
					<div className="flex justify-between gap-5 p-[21px]">
						<Button
							color="light"
							className="w-full"
							onClick={() => {
								setSearchParams({});
								setAppliedFilters([]);
								setShowFilterDrawer(false);
							}}
						>
							Clear
						</Button>
						<Button color="purplePlaced" className="w-full" onClick={() => setShowFilterDrawer(false)}>
							Apply
						</Button>
					</div>
				}
			/>
		</>
	);
};

export default RenderFilters;
